import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, Row, Col, Typography, message, Spin, Alert, Collapse, Tag, Progress } from 'antd';
import { ExperimentOutlined, HeartOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import SystemMediatedSimulationEngine from '../simulation/core/SystemMediatedSimulationEngine';
import { ParameterControl } from '../simulation/ui/ParameterControls';
import { PARAMETER_CLASSIFICATION, getSliderParameters } from '../simulation/core/ParameterClassification';
import { buildParameterRegistry } from '../utils/parameterSearchUtils';
import SVGCoxcombChart from './SVGCoxcombChart';
import { calculateDynamicTarget, calculateZScore_V2 } from '../utils/scoringEngineV2';
import { calculateDynamicSliderRange, getSliderDefaultValue } from '../simulation/utils/DynamicRangeCalculator';
import usePriorityParameters from '../hooks/usePriorityParameters';
import { Select, Button, Input } from 'antd';

// Import configuration files directly (fallback to HTTP if imports fail)
let vitalsConfigData, labsConfigData, biometricsConfigData, vitalsWeightsData, labsWeightsData, targetFormulasData;

try {
  vitalsConfigData = require('../configs/scoring_config_vitals.json');
  labsConfigData = require('../configs/scoring_config_labs.json');
  biometricsConfigData = require('../configs/scoring_config_biometrics.json');
  vitalsWeightsData = require('../configs/parameter_weights_vitals.json');
  labsWeightsData = require('../configs/parameter_weights_labs.json');
  targetFormulasData = require('../configs/parameter_target_formulas_vn.json');
} catch (error) {
  // console.warn('[SystemMediatedSimulation] Failed to import config files, will use HTTP fallback:', error);
}

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

// Helper functions for enhanced categorization using actual parameter categories
const getCategoryIcon = (category) => {
  const icons = {
    'Vitals': '💓',
    'Physical Exam': '🩺',
    'Biometrics': '📏',
    'Lab Results': '🧪',
    'Imaging': '🔬'
  };
  return <span style={{ fontSize: '16px', marginRight: '4px' }}>{icons[category] || '📋'}</span>;
};

const getCategoryColor = (category) => {
  const colors = {
    'Vitals': 'red',
    'Physical Exam': 'blue',
    'Biometrics': 'green',
    'Lab Results': 'orange',
    'Imaging': 'purple'
  };
  return colors[category] || 'default';
};

// Helper function for health system colors in Coxcomb chart
const getSystemColor = (systemName) => {
  const systemColors = {
    'cardiovascular': '#ff4d4f',
    'respiratory': '#52c41a',
    'metabolic': '#1890ff',
    'filtration': '#722ed1',
    'neurological': '#fa8c16',
    'musculoskeletal': '#13c2c2',
    'immune': '#eb2f96'
  };
  return systemColors[systemName.toLowerCase()] || '#8c8c8c';
};

/**
 * System-Mediated Simulation Sandbox Component - ENHANCED DYNAMIC RANGE VERSION
 *
 * CURRENT STATE: Enhanced Systolic Blood Pressure slider with dynamic ranges
 *
 * This enhanced version demonstrates patient-specific dynamic range calculation
 * using the exact same HealthSnapshotV2 methodology for perfect consistency.
 *
 * Enhanced Features:
 * - Patient-specific dynamic ranges: idealTarget ± (6.0 × standardDeviation)
 * - Uses same calculateDynamicTarget() function as HealthSnapshotV2
 * - Extracts standard deviation from same scoring configuration files
 * - Real-time slider updates with 25ms debouncing
 * - Integration with HealthSnapshotV2 calculateZScore_V2 methodology
 * - Immediate coxcomb chart updates reflecting health score changes
 * - Proper parameter naming following parameters.md conventions (vitals_sbp)
 * - System-mediated health improvements propagation
 * - Dynamic range calculation with safety bounds (50-300 mmHg)
 * - Visual indicators showing dynamic vs static ranges
 *
 * This creates an enhanced foundation that demonstrates how to implement
 * clinically personalized parameter controls for the simulation sandbox.
 */
const SystemMediatedSimulationSandbox = ({ patientId }) => {
  const { isAuthenticated, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState(null);
  const [sliderValues, setSliderValues] = useState({});
  const [baselineValues, setBaselineValues] = useState({});
  const [patientDemographics, setPatientDemographics] = useState({});
  const [simulationResults, setSimulationResults] = useState(null);
  const [dynamicRanges, setDynamicRanges] = useState({});
  const [parameterStatuses, setParameterStatuses] = useState({});
  const debounceTimer = useRef(null);
  const [simulationEngine] = useState(new SystemMediatedSimulationEngine());
  const isMountedRef = useRef(true);
  const { loading: priorityLoading, error: priorityError, priorityParameters } = usePriorityParameters(patientId);
  const [simulationParameters, setSimulationParameters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedParameter, setSelectedParameter] = useState(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (priorityParameters.length > 0) {
      const initialParams = getSliderParameters().filter(p => priorityParameters.some(pp => pp.key === p.key));
      if (isMountedRef.current) {
        setSimulationParameters(initialParams);
      }
    }
  }, [priorityParameters]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'High Priority Action':
        return '#ff4d4f'; // red
      case 'Moderate Priority Action':
        return '#faad14'; // orange
      case 'Low Priority Action':
        return '#52c41a'; // green
      default:
        return '#d9d9d9'; // grey
    }
  };

  const handleAddParameter = () => {
    if (selectedParameter) {
      const paramToAdd = getSliderParameters().find(p => p.key === selectedParameter);
      if (paramToAdd && !simulationParameters.some(p => p.key === selectedParameter)) {
        setSimulationParameters(prevParams => [...prevParams, paramToAdd]);
      }
      setSelectedParameter(null);
      setSearchTerm('');
    }
  };

  const availableParameters = getSliderParameters().filter(p =>
    !simulationParameters.some(sp => sp.key === p.key) &&
    (p.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
     (p.displayName && p.displayName.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  // Helper function to provide category descriptions
  const getCategoryDescription = (category) => {
    const descriptions = {
      'Vitals': 'Blood pressure, heart rate, temperature, and other vital signs',
      'Physical Exam': 'Physical examination findings and assessments',
      'Biometrics': 'Body measurements, composition, and physical performance',
      'Lab Results': 'Blood tests, chemistry panels, and laboratory values',
      'Imaging': 'Radiology and diagnostic imaging results'
    };
    return descriptions[category] || '';
  };

  // Enhanced parameter categorization using actual categories from parameters.md
  const parameterGroups = useMemo(() => {
    const groups = {};

    // Build parameter registry to get actual category assignments
    const parameterRegistry = buildParameterRegistry();
    const parameterCategoryMap = new Map();
    parameterRegistry.forEach(param => {
      parameterCategoryMap.set(param.key, param.category);
    });

    // Filter out lifestyle parameters to focus on health systems only
    const healthSystemsParameters = simulationParameters.filter(param => {
      const actualCategory = parameterCategoryMap.get(param.key);
      // Exclude lifestyle categories and any parameters that start with lifestyle_
      return actualCategory !== 'Lifestyle' && !param.key.startsWith('lifestyle_');
    });

    // Group parameters using actual category assignments from parameters.md
    healthSystemsParameters.forEach(param => {
      // Get the actual category from the parameter registry
      const category = parameterCategoryMap.get(param.key) || 'Uncategorized';

      if (!groups[category]) {
        groups[category] = {
          title: category,
          parameters: [],
          description: getCategoryDescription(category)
        };
      }
      groups[category].parameters.push(param);
    });

    // Sort parameters within each group by priority (if available) then alphabetically
    Object.values(groups).forEach(group => {
      group.parameters.sort((a, b) => {
        // First sort by priority status if available
        const aStatus = parameterStatuses[a.key];
        const bStatus = parameterStatuses[b.key];
        const statusPriority = { 'High Priority Action': 3, 'Moderate Priority Action': 2, 'Low Priority Action': 1 };

        const aPriority = statusPriority[aStatus] || 0;
        const bPriority = statusPriority[bStatus] || 0;

        if (aPriority !== bPriority) {
          return bPriority - aPriority; // Higher priority first
        }

        // Then sort alphabetically by display name
        const aName = a.displayName || a.key;
        const bName = b.displayName || b.key;
        return aName.localeCompare(bName);
      });
    });

    return groups;
  }, [simulationParameters, parameterStatuses]);

  // Calculate parameter status based on Z-score relative to ideal target
  const calculateParameterStatus = useCallback((paramKey, currentValue, configs, patientDemographics) => {
    if (!configs || !configs.allScoringConfig || !configs.targetFormulas || !configs.parameterWeights) {
      return 'Low Priority Action'; // Fallback if configs not loaded
    }

    try {
      // Find parameter configuration
      const paramConfig = configs.allScoringConfig.find(p => p.key === paramKey);
      if (!paramConfig) return 'Low Priority Action';

      // Calculate dynamic target using HealthSnapshotV2 function
      const targetResult = calculateDynamicTarget(paramKey, patientDemographics, configs.targetFormulas);
      if (targetResult.error) return 'Low Priority Action';

      // Calculate Z-score using HealthSnapshotV2 function
      const zScoreResult = calculateZScore_V2(currentValue, targetResult.value, paramConfig);
      if (zScoreResult.error) return 'Low Priority Action';

      const absZScore = Math.abs(zScoreResult.zScore);
      const weightInfo = configs.parameterWeights[paramKey];
      const weight = weightInfo?.overall_clinical_significance_weight ?? 0;
      const weightedAbsoluteZScore = absZScore * (weight / 100);

      const { highPriority, moderatePriority } = configs.priorityThresholds;

      if (weightedAbsoluteZScore >= highPriority.impactScoreThreshold) return 'High Priority Action';
      if (weightedAbsoluteZScore >= moderatePriority.impactScoreThreshold) return 'Moderate Priority Action';
      return 'Low Priority Action';

    } catch (error) {
      console.warn(`[SystemMediatedSimulation] Error calculating status for ${paramKey}:`, error);
      return 'Low Priority Action';
    }
  }, []);

  // Load configuration data
  const loadConfigurations = async () => {
    // First, try to use imported configuration data if available
    if (vitalsConfigData && labsConfigData && biometricsConfigData && vitalsWeightsData && labsWeightsData && targetFormulasData) {
      console.log('[SystemMediatedSimulation] Using imported configuration files');
      return {
        allScoringConfig: [
          ...(vitalsConfigData.parameters || []),
          ...(labsConfigData.parameters || []),
          ...(biometricsConfigData.parameters || [])
        ],
        parameterWeights: {
          ...(vitalsWeightsData.parameters || {}),
          ...(labsWeightsData.parameters || {})
        },
        targetFormulas: targetFormulasData || [],
        priorityThresholds: { highPriority: { impactScoreThreshold: 2.0 }, moderatePriority: { impactScoreThreshold: 1.0 } }
      };
    }

    // Fallback to HTTP requests if imports failed
    try {
      console.log('[SystemMediatedSimulation] Imported configs not available, trying HTTP fallback');
      // Load all configuration files in parallel
      const [vitalsConfig, labsConfig, biometricsConfig, weightsVitals, weightsLabs, targetFormulas] = await Promise.all([
        axios.get('/configs/scoring_config_vitals.json'),
        axios.get('/configs/scoring_config_labs.json'),
        axios.get('/configs/scoring_config_biometrics.json'),
        axios.get('/configs/parameter_weights_vitals.json'),
        axios.get('/configs/parameter_weights_labs.json'),
        axios.get('/configs/parameter_target_formulas_vn.json')
      ]);

      // Check if all requests were successful
      if (vitalsConfig.data && labsConfig.data && biometricsConfig.data && weightsVitals.data && weightsLabs.data && targetFormulas.data) {
        console.log('[SystemMediatedSimulation] Successfully loaded configurations via HTTP');
        return {
          allScoringConfig: [
            ...(vitalsConfig.data.parameters || []),
            ...(labsConfig.data.parameters || []),
            ...(biometricsConfig.data.parameters || [])
          ],
          parameterWeights: {
            ...(weightsVitals.data.parameters || {}),
            ...(labsWeightsData.parameters || {})
          },
          targetFormulas: targetFormulas.data || [],
          priorityThresholds: { highPriority: { impactScoreThreshold: 2.0 }, moderatePriority: { impactScoreThreshold: 1.0 } }
        };
      }
    } catch (error) {
      console.error('Failed to load configurations via HTTP:', error);

      // Provide detailed error information
      if (error.response) {
        console.error('Configuration loading error details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          url: error.config?.url,
          message: error.message
        });

        // Show user-friendly error message
        if (error.response.status === 404) {
          console.warn('Configuration files not found via HTTP. This is expected in production.');
        } else {
          message.error(`Failed to load configuration files: ${error.response.status} ${error.response.statusText}`);
        }
      } else if (error.request) {
        console.error('Network error loading configurations:', error.request);
        console.warn('Network error loading configurations via HTTP. This may be expected in production.');
      } else {
        console.error('Configuration loading error:', error.message);
        message.error('Error loading configuration files. Please try again.');
      }
    }

    // Final fallback - return empty configuration to prevent complete failure
    console.warn('[SystemMediatedSimulation] All configuration loading methods failed, using empty fallback');
    message.warning('Configuration files could not be loaded. Simulation may have limited functionality.');
    return {
      allScoringConfig: [],
      parameterWeights: {},
      targetFormulas: [],
      priorityThresholds: { highPriority: { impactScoreThreshold: 2.0 }, moderatePriority: { impactScoreThreshold: 1.0 } }
    };
  };

  // Load patient data
  const loadPatientData = useCallback(async (patientId) => {
    // Check authentication first
    if (!isAuthenticated || !token) {
      // console.warn('User not authenticated - cannot load patient data');
      message.warning('Please log in to access patient data for simulation.');
      return {};
    }

    try {
      const [flowsheetRes, demographicsRes] = await Promise.all([
        axios.get(`/patients/${patientId}/flowsheet/latest-for-simulation`),
        axios.get(`/patients/${patientId}/comprehensive-scoring-data`),
      ]);

      const patientData = flowsheetRes.data.data || {};
      const demographics = demographicsRes.data.data.demographics || {};

      return { ...patientData, demographics };
    } catch (error) {
      console.error('Failed to load patient data:', error);

      if (error.response) {
        console.error('Patient data loading error details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          url: error.config?.url,
          message: error.response.data?.message
        });

        if (error.response.status === 404) {
          message.warning('Patient data not found. Using default values for simulation.');
        } else if (error.response.status === 401) {
          message.error('Authentication failed. Please log in again to access patient data.');
          console.error('401 Unauthorized - Token may be expired or invalid');
        } else if (error.response.status === 403) {
          message.error('Access denied. You may not have permission to view this patient data.');
        } else {
          message.warning(`Could not load patient data (${error.response.status}). Using default values for simulation.`);
        }
      } else if (error.request) {
        console.error('Network error loading patient data:', error.request);
        message.warning('Network error: Could not connect to server for patient data.');
      } else {
        console.error('Patient data loading error:', error.message);
        message.warning('Error loading patient data. Using default values for simulation.');
      }

      return {};
    }
  }, [isAuthenticated, token]);

  // Run system-mediated simulation
  const runSystemMediatedSimulation = useCallback(async (newSliderValues, currentBaselines, currentConfigs, demographics = null) => {
    // Validate all required inputs before proceeding
    if (!currentConfigs?.allScoringConfig) {
      console.warn('[SystemMediatedSimulation] Cannot run simulation: missing configurations');
      return;
    }

    if (!newSliderValues || !currentBaselines) {
      console.warn('[SystemMediatedSimulation] Cannot run simulation: missing parameter values');
      return;
    }

    try {
      // Use passed demographics or fall back to state
      const demoToUse = demographics || patientDemographics;

      // Additional validation for demographics
      if (!demoToUse || typeof demoToUse !== 'object') {
        console.warn('[SystemMediatedSimulation] Cannot run simulation: invalid demographics');
        return;
      }

      // Run system-mediated simulation with additional error checking
      const results = await simulationEngine.runSystemMediatedSimulation(
        newSliderValues,
        currentBaselines,
        demoToUse, // Use real patient demographics
        currentConfigs.allScoringConfig,
        currentConfigs.parameterWeights,
        currentConfigs.targetFormulas
      );

      // Validate results before setting state
      if (!results) {
        console.error('[SystemMediatedSimulation] Simulation returned null/undefined results');
        message.error('Simulation calculation failed - no results returned');
        return;
      }

      if (results.error) {
        console.error('[SystemMediatedSimulation] Simulation error:', results.error);
        message.error('Simulation calculation failed');
        return;
      }

      // Only update state if component is still mounted and we have valid results
      if (isMountedRef.current && results.finalSystemScores && typeof results.finalSystemScores === 'object') {
        if (isMountedRef.current) {
          setSimulationResults(results);
        }
      } else if (!isMountedRef.current) {
        console.warn('[SystemMediatedSimulation] Component unmounted, skipping state update');
      } else {
        console.error('[SystemMediatedSimulation] Invalid simulation results structure:', results);
        message.error('Simulation returned invalid results');
      }

    } catch (error) {
      console.error('[SystemMediatedSimulation] Simulation failed:', error);
      message.error('Simulation failed to execute');

      // Don't let the error crash the component
      // Reset to a safe state if needed
      if (error.name === 'TypeError' || error.message.includes('Cannot read property')) {
        console.warn('[SystemMediatedSimulation] Resetting simulation state due to error');
        setSimulationResults(null);
      }
    }
  }, [simulationEngine]);

  // Initialize simulation
  useEffect(() => {
    const initializeSimulation = async () => {
      try {
        setLoading(true);
        const [configsResponse, patientDataResponse] = await Promise.all([
          loadConfigurations(),
          loadPatientData(patientId),
        ]);
        if (!configsResponse || !patientDataResponse) {
          throw new Error('Failed to load configurations or patient data.');
        }
        setConfigs(configsResponse);
        setPatientDemographics(patientDataResponse.demographics || {});
      } catch (error) {
        console.error('Failed to initialize system-mediated simulation:', error);
        message.error('Failed to initialize simulation sandbox. Please check console for details.');
      } finally {
        setLoading(false);
      }
    };
    if (patientId && isAuthenticated) {
      initializeSimulation();
    } else {
      setLoading(false);
    }
  }, [patientId, isAuthenticated]);

  useEffect(() => {
    if (!configs || !patientDemographics || simulationParameters.length === 0) {
      return;
    }
    const initializeNewParameters = async () => {
      const newParameters = simulationParameters.filter(p => !sliderValues.hasOwnProperty(p.key));
      if (newParameters.length > 0) {
        const patientDataResponse = await loadPatientData(patientId);
        const initialValues = { ...sliderValues };
        const initialBaselines = { ...baselineValues };
        newParameters.forEach(param => {
          const paramKey = param.key;
          const patientValue = patientDataResponse[paramKey];
          const config = PARAMETER_CLASSIFICATION[paramKey];
          const defaultValue = patientValue || config?.defaultValue || config?.range?.min || 0;
          initialValues[paramKey] = defaultValue;
          initialBaselines[paramKey] = defaultValue;
        });
        setSliderValues(initialValues);
        setBaselineValues(initialBaselines);
        await runSystemMediatedSimulation(initialValues, initialBaselines, configs, patientDemographics);
      }
    };
    initializeNewParameters();
  }, [simulationParameters, configs, patientDemographics]);







  // Handle parameter changes with debouncing
  const handleParameterChange = useCallback((paramKey, value) => {
    // Update slider values immediately for responsive UI
    setSliderValues(prevValues => {
      const newValues = { ...prevValues, [paramKey]: value };

      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Set new debounced timer for simulation
      debounceTimer.current = setTimeout(() => {
        // Use current state values at the time of execution to avoid stale closures
        // Get fresh values from state instead of relying on closure
        const currentBaselines = baselineValues;
        const currentConfigs = configs;
        const currentDemographics = patientDemographics;

        // Only run simulation if we have valid configurations
        if (currentConfigs?.allScoringConfig && currentBaselines && currentDemographics) {
          runSystemMediatedSimulation(newValues, currentBaselines, currentConfigs, currentDemographics);
        } else {
          console.warn('[SystemMediatedSimulation] Skipping simulation due to missing data:', {
            hasConfigs: !!currentConfigs?.allScoringConfig,
            hasBaselines: !!currentBaselines,
            hasDemographics: !!currentDemographics
          });
        }
      }, 25); // 25ms debouncing as specified in requirements

      return newValues;
    });

    // Update parameter status separately to avoid cascading state updates
    if (['vitals_sbp', 'vitals_rhr', 'vitals_dbp'].includes(paramKey) && configs && patientDemographics && isMountedRef.current) {
      try {
        const newStatus = calculateParameterStatus(paramKey, value, configs, patientDemographics);
        if (isMountedRef.current) {
          setParameterStatuses(prevStatuses => ({
            ...prevStatuses,
            [paramKey]: newStatus
          }));
        }
      } catch (error) {
        console.error('[SystemMediatedSimulation] Error calculating parameter status:', error);
      }
    }
  }, [baselineValues, configs, runSystemMediatedSimulation, calculateParameterStatus]);

  // Patient-friendly parameter control with explanatory context
  const renderParameterControl = (param) => {
    const config = PARAMETER_CLASSIFICATION[param.key];
    const value = sliderValues[param.key];
    const systemMediatedEffect = simulationResults?.systemMediatedEffects?.[param.key];
    const patientValue = baselineValues[param.key];

    // Debug logging for core vitals parameters to ensure visual indicators work
    if (['vitals_sbp', 'vitals_dbp', 'vitals_rhr'].includes(param.key)) {
      console.log(`[SimulationSandbox] Visual indicators data for ${param.key}:`, {
        patientValue,
        currentSliderValue: value,
        dynamicRange: dynamicRanges[param.key],
        hasPatientData: patientValue !== null && patientValue !== undefined
      });
    }

    // Get patient-friendly parameter information
    const getParameterInfo = (paramKey) => {
      switch (paramKey) {
        case 'vitals_sbp':
          return {
            friendlyName: 'Blood Pressure (Top Number)',
            explanation: 'This is the pressure when your heart beats. Lower numbers are usually better for your health.',
            goodRange: 'Ideal: 90-120',
            currentStatus: value < 120 ? 'Great!' : value < 140 ? 'Good' : 'Could be better'
          };
        case 'vitals_dbp':
          return {
            friendlyName: 'Blood Pressure (Bottom Number)',
            explanation: 'This is the pressure when your heart rests between beats. Lower is generally better.',
            goodRange: 'Ideal: 60-80',
            currentStatus: value < 80 ? 'Great!' : value < 90 ? 'Good' : 'Could be better'
          };
        case 'vitals_rhr':
          return {
            friendlyName: 'Resting Heart Rate',
            explanation: 'The number of times your heart beats per minute at rest. A lower rate often indicates better cardiovascular fitness.',
            goodRange: 'Ideal: 50-70 bpm',
            currentStatus: value < 70 ? 'Great!' : value < 85 ? 'Good' : 'Could be better'
          };
        default:
          return {
            friendlyName: config?.displayName || paramKey,
            explanation: 'Adjust this value to see how it affects your health.',
            goodRange: 'See slider for healthy range',
            currentStatus: 'Adjustable'
          };
      }
    };

    const paramInfo = getParameterInfo(param.key);

    return (
      <div key={param.key} style={{ marginBottom: '20px' }}>
        {/* Patient-friendly header */}
        <div style={{ marginBottom: '12px' }}>
          <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
            {paramInfo.friendlyName}
          </Text>
          <div style={{ marginTop: '4px' }}>
            <Text style={{ fontSize: '13px', color: '#666' }}>
              {paramInfo.explanation}
            </Text>
          </div>
          <div style={{ marginTop: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {paramInfo.goodRange}
            </Text>
          </div>
        </div>

        {/* Technical parameter control */}
        <div style={{
          background: '#fafafa',
          padding: '16px',
          borderRadius: '6px',
          border: '1px solid #f0f0f0'
        }}>
          <ParameterControl
            key={param.key}
            paramKey={param.key}
            config={config}
            value={value}
            onChange={handleParameterChange}
            // Enhanced props for dynamic range calculation
            patientDemographics={patientDemographics}
            targetFormulas={configs?.targetFormulas}
            scoringConfig={configs?.allScoringConfig}
            patientData={patientValue}
            // Pass dynamic range and status for vitals_sbp
            dynamicRange={dynamicRanges[param.key]}
            parameterStatus={parameterStatuses[param.key]}
            statusColor={getStatusColor(parameterStatuses[param.key])}
          />
        </div>

        {/* Show current vs baseline comparison */}
        {patientValue && value !== patientValue && (
          <div style={{
            marginTop: '8px',
            padding: '8px 12px',
            background: value < patientValue ? '#f6ffed' : '#fff2e8',
            borderRadius: '4px',
            border: `1px solid ${value < patientValue ? '#b7eb8f' : '#ffbb96'}`
          }}>
            <Text style={{ fontSize: '12px', color: value < patientValue ? '#389e0d' : '#d4380d' }}>
              {value < patientValue ? '📉 Lower than your current' : '📈 Higher than your current'} ({patientValue} {config?.unit})
              {value < patientValue && param.key === 'vitals_sbp' && ' - This could improve your heart health!'}
            </Text>
          </div>
        )}
      </div>
    );
  };

  // Patient-friendly health impact summary with unified list and progress bars
  const renderPatientFriendlyImpactSummary = () => {
    if (!simulationResults?.baselineSystemScores || !simulationResults?.finalSystemScores || !simulationResults?.systemImprovements) {
      return (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: '18px', marginRight: '8px' }}>✨</span>
              <span style={{ color: '#1890ff', fontWeight: 'bold' }}>Health Changes</span>
            </div>
          }
          style={{
            background: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            border: '1px solid #e8e8e8'
          }}
        >
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🎯</div>
            <Text style={{ fontSize: '14px', color: '#666' }}>
              Make changes above to see how they improve your health
            </Text>
          </div>
        </Card>
      );
    }

    const systemKeys = Object.keys(simulationResults.finalSystemScores);
    if (systemKeys.length === 0) {
      return null;
    }

    return (
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '18px', marginRight: '8px' }}>✨</span>
            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>Health Changes</span>
          </div>
        }
        style={{
          background: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e8e8e8'
        }}
      >
        <div style={{ marginBottom: '12px' }}>
          <Text style={{ fontSize: '14px', color: '#666' }}>
            See how your changes affect each area of your health:
          </Text>
        </div>

        {/* Unified list of all health systems */}
        {systemKeys.map(systemKey => {
          const baseline = simulationResults.baselineSystemScores[systemKey]?.score || 0;
          const final = simulationResults.finalSystemScores[systemKey]?.score || 0;
          const improvement = final - baseline;
          const baselineZ = simulationResults.baselineSystemScores[systemKey]?.dimensionDetails.structure.averageWeightedAbsZ + simulationResults.baselineSystemScores[systemKey]?.dimensionDetails.function.averageWeightedAbsZ + simulationResults.baselineSystemScores[systemKey]?.dimensionDetails.risk.averageWeightedAbsZ || 0;
          const finalZ = simulationResults.finalSystemScores[systemKey]?.dimensionDetails.structure.averageWeightedAbsZ + simulationResults.finalSystemScores[systemKey]?.dimensionDetails.function.averageWeightedAbsZ + simulationResults.finalSystemScores[systemKey]?.dimensionDetails.risk.averageWeightedAbsZ || 0;
          const zScoreChange = Math.abs(finalZ - baselineZ);
          const sources = simulationResults.systemImprovements[systemKey]?.sources.map(s => s.parameter).join(', ') || 'N/A';

          // Patient-friendly system names
          const getSystemDisplayName = (key) => {
            const nameMap = {
              'cardiovascular': 'Heart & Blood Vessels',
              'respiratory': 'Lungs & Breathing',
              'neurological': 'Brain & Nervous System',
              'metabolic': 'Metabolism & Energy',
              'filtration': 'Kidneys & Filtering',
              'musculoskeletal': 'Muscles & Bones',
              'immune': 'Immune System'
            };
            return nameMap[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
          };

          // Get friendly names for source parameters
          const sourceDisplayNames = simulationResults.systemImprovements[systemKey]?.sources
            .map(s => PARAMETER_CLASSIFICATION[s.parameter]?.displayName || s.parameter)
            .join(', ');

          return (
            <div key={systemKey} style={{
              marginBottom: '16px',
              padding: '16px',
              background: '#fafafa',
              borderRadius: '6px',
              border: '1px solid #f0f0f0'
            }}>
              <Row justify="space-between" align="middle" style={{ marginBottom: '8px' }}>
                <Col span={12}>
                  <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                    {getSystemDisplayName(systemKey)}
                  </Text>
                  <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '2px' }}>
                    Affected by your changes to {sourceDisplayNames}
                  </div>
                </Col>
                <Col span={8}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {baseline.toFixed(1)}%
                  </Text>
                  <Text strong style={{ fontSize: '12px' }}>
                    {' → '}{final.toFixed(1)}%
                  </Text>
                </Col>
                <Col span={4} style={{ textAlign: 'right' }}>
                  <Tag color={improvement > 0 ? 'green' : improvement < 0 ? 'red' : 'default'} style={{ fontSize: '10px' }}>
                    {improvement > 0 ? '+' : ''}{improvement.toFixed(1)}%
                  </Tag>
                </Col>
              </Row>

              {/* Progress bar showing magnitude of change */}
              <Progress
                percent={Math.min(100, Math.abs(improvement) * 1)}
                size="small"
                status={improvement > 0 ? 'success' : improvement < 0 ? 'exception' : 'normal'}
                showInfo={false}
                style={{ marginTop: '4px' }}
              />

            </div>
          );
        })}
      </Card>
    );
  };



  if (loading) {
    return (
      <Spin tip="Initializing System-Mediated Simulation..." size="large" style={{ display: 'block', marginTop: '50px' }} />
    );
  }

  if (!isAuthenticated) {
    return (
      <Card title={
        <>
          <ExperimentOutlined style={{ marginRight: '8px' }} />
          System-Mediated Health Simulation
        </>
      }>
        <Alert
          message="Authentication Required"
          description="Please log in to access the health simulation sandbox with real patient data."
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      </Card>
    );
  }

  return (
    <div style={{ background: '#fafafa', minHeight: '100vh', padding: '20px' }}>
      {/* Patient-Friendly Header */}
      <Card
        style={{
          marginBottom: '20px',
          background: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e8e8e8'
        }}
      >
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <Title level={2} style={{ color: '#1890ff', marginBottom: '8px' }}>
            🔬 Health Improvement Simulator
          </Title>
          <Paragraph style={{ fontSize: '16px', color: '#666', marginBottom: '8px' }}>
            See how small changes to your health numbers can improve your overall wellness
          </Paragraph>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Powered by advanced health calculations • Safe simulation environment
          </Text>
        </div>
      </Card>

      {/* Redesigned Layout: Two-Panel Immersive Simulation */}
      <Row gutter={24} style={{ minHeight: 'calc(100vh - 200px)' }}>

        {/* Left Panel: Control Panel */}
        <Col xs={24} md={9} lg={9} style={{ display: 'flex', flexDirection: 'column' }}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px' }}>🎛️</span>
                  <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                    Parameter Controls
                  </span>
                  <Tag color="orange">{simulationParameters.length} active</Tag>
                </div>
                <Button
                  type="text"
                  size="small"
                  icon={<span>📋</span>}
                  style={{ color: '#666' }}
                >
                  Manage
                </Button>
              </div>
            }
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
            bodyStyle={{
              flex: 1,
              overflow: 'auto',
              padding: '16px'
            }}
          >
            <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
              <Select
                showSearch
                value={selectedParameter}
                onChange={setSelectedParameter}
                onSearch={setSearchTerm}
                placeholder="Add a parameter to explore..."
                style={{ flex: 1 }}
                filterOption={false}
                size="small"
              >
                {availableParameters.map(p => (
                  <Select.Option key={p.key} value={p.key}>
                    {p.displayName || p.key}
                  </Select.Option>
                ))}
              </Select>
              <Button
                icon={<PlusOutlined />}
                onClick={handleAddParameter}
                type="primary"
                size="small"
              >
                Add
              </Button>
            </div>
            <div style={{
              background: '#e6f7ff',
              padding: '12px 16px',
              borderRadius: '6px',
              marginBottom: '16px',
              border: '1px solid #91d5ff'
            }}>
              <Text style={{ color: '#0050b3', fontSize: '13px' }}>
                <strong>💡 How it works:</strong> Adjust the sliders below to see how changes to your health numbers
                could improve different areas of your health. Watch the charts update in real-time!
              </Text>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {Object.entries(parameterGroups).map(([groupKey, group]) => (
                <Collapse
                  key={groupKey}
                  size="small"
                  style={{ marginBottom: '8px' }}
                  items={[
                    {
                      key: groupKey,
                      label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {getCategoryIcon(groupKey)}
                          <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                            {group.title}
                          </span>
                          <Tag color={getCategoryColor(groupKey)} size="small">
                            {group.parameters.length}
                          </Tag>
                        </div>
                      ),
                      children: (
                        <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                          {group.parameters.map(param => (
                            <div key={param.key} style={{ marginBottom: '12px' }}>
                              {renderParameterControl(param)}
                            </div>
                          ))}
                        </div>
                      )
                    }
                  ]}
                  defaultActiveKey={Object.keys(parameterGroups).slice(0, 2)}
                />
              ))}
            </div>
          </Card>
        </Col>

        {/* Right Panel: Impact Visualization */}
        <Col xs={24} md={15} lg={15}>
          <div style={{ position: 'sticky', top: '20px' }}>
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>📊</span>
                  <span style={{ color: '#1890ff', fontWeight: 'bold', fontSize: '18px' }}>
                    Health Impact Visualization
                  </span>
                  <Tag color="blue">Live Simulation</Tag>
                </div>
              }
              style={{
                height: 'fit-content',
                minHeight: '600px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                border: '2px solid #e6f7ff',
              }}
            >
              <Row gutter={[16, 16]} style={{ height: '100%' }}>
                <Col xs={24} lg={12}>
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '16px',
                    height: '500px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {simulationResults ? (
                      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Title level={4} style={{ margin: '0 0 16px 0', color: '#1890ff' }}>
                          Your Health Systems
                        </Title>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                          <SVGCoxcombChart
                            healthSystemsData={Object.entries(simulationResults.finalSystemScores)
                              .filter(([name, data]) => data && typeof data.score === 'number' && !isNaN(data.score))
                              .map(([name, data]) => ({
                                name: name.charAt(0).toUpperCase() + name.slice(1),
                                score: Math.round(Math.max(0, Math.min(100, data.score))),
                                color: getSystemColor(name)
                              }))}
                            chartType="systems"
                            size={300}
                            showLabels={true}
                            showPercentages={true}
                          />
                        </div>
                        <div style={{ width: '100%', marginTop: '16px' }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '8px',
                            background: '#f0f2f5',
                            borderRadius: '4px'
                          }}>
                            <Text strong style={{ marginRight: '16px', fontSize: '12px' }}>Health Score Legend:</Text>
                            <div style={{
                              width: '150px',
                              height: '10px',
                              background: 'linear-gradient(to right, #ff4d4f, #faad14, #52c41a)',
                              borderRadius: '5px',
                              marginRight: '8px'
                            }}></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '150px', fontSize: '10px' }}>
                              <Text type="secondary">Low</Text>
                              <Text type="secondary">High</Text>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', color: '#666' }}>
                        <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>📊</span>
                        <Text style={{ fontSize: '16px' }}>
                          No health data available to display chart.
                        </Text>
                      </div>
                    )}
                  </div>
                </Col>
                <Col xs={24} lg={12}>
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '16px',
                    height: '500px',
                    overflow: 'auto'
                  }}>
                    {renderPatientFriendlyImpactSummary()}
                  </div>
                </Col>
              </Row>
            </Card>
          </div>
        </Col>

      </Row>
    </div>
  );
};

export default SystemMediatedSimulationSandbox;
